import {
  getDaysInMonth as getFnsDaysInMonth,
  getDay,
  startOfDay,
  isSameDay,
  isWithinInterval,
  isAfter as fnsIsAfter,
  isBefore as fnsIsBefore,
  format,
} from 'date-fns';

/**
 * Date calculation and formatting helpers for DateRangeTimePicker
 */

/**
 * Gets the number of days in a month
 * @param {number} month - Month (0-11)
 * @param {number} year - Year
 * @returns {number} Number of days in the month
 */
export const getDaysInMonth = (month, year) => {
  // NOTE(vlad|SPD-14091:2026-02-05): date-fns expects month to be 0-indexed, which matches our input
  return getFnsDaysInMonth(new Date(year, month));
};

/**
 * Gets the day of week for the first day of a month (0 = Sunday)
 * @param {number} month - Month (0-11)
 * @param {number} year - Year
 * @returns {number} Day of week (0-6)
 */
export const getFirstDayOfMonth = (month, year) => {
  return getDay(new Date(year, month, 1));
};

/**
 * Checks if a date is in the current range
 * @param {Date} date - Date to check
 * @param {Date} startDate - Range start
 * @param {Date} endDate - Range end
 * @returns {boolean} True if in range
 */
export const isDateInRange = (date, startDate, endDate) => {
  if (!startDate || !endDate) {
    return false;
  }

  const dateStart = startOfDay(date);
  const rangeStart = startOfDay(startDate);
  const rangeEnd = startOfDay(endDate);

  return isWithinInterval(dateStart, { start: rangeStart, end: rangeEnd });
};

/**
 * Checks if a date is the start of the range
 * @param {Date} date - Date to check
 * @param {Date} startDate - Range start
 * @returns {boolean} True if start date
 */
export const isStartDate = (date, startDate) => {
  return startDate && isSameDay(date, startDate);
};

/**
 * Checks if a date is the end of the range
 * @param {Date} date - Date to check
 * @param {Date} endDate - Range end
 * @returns {boolean} True if end date
 */
export const isEndDate = (date, endDate) => {
  return endDate && isSameDay(date, endDate);
};

/**
 * Checks if a date is between start and end (exclusive)
 * @param {Date} date - Date to check
 * @param {Date} startDate - Range start
 * @param {Date} endDate - Range end
 * @returns {boolean} True if between
 */
export const isDateBetween = (date, startDate, endDate) => {
  if (!startDate || !endDate) {
    return false;
  }

  const dateStart = startOfDay(date);
  const rangeStart = startOfDay(startDate);
  const rangeEnd = startOfDay(endDate);

  return fnsIsAfter(dateStart, rangeStart) && fnsIsBefore(dateStart, rangeEnd);
};

/**
 * Checks if a date is today
 * @param {Date} date - Date to check
 * @returns {boolean} True if today
 */
export const isToday = (date) => {
  return isSameDay(date, new Date());
};

/**
 * Formats a date range for display
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {string} Formatted date range
 */
export const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return '';
  }

  const startDay = format(startDate, 'd');
  const startMonth = format(startDate, 'MMM');
  const endDay = format(endDate, 'd');
  const endMonth = format(endDate, 'MMM');
  const year = format(endDate, 'yyyy');

  return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
};

/**
 * Formats a single date for display
 * @param {Date} date - Date to format
 * @returns {string} Formatted date (e.g., "4 Feb 2026")
 */
export const formatSingleDate = (date) => {
  if (!date) {
    return '';
  }

  return `${format(date, 'd')} ${format(date, 'MMM')} ${format(date, 'yyyy')}`;
};

/**
 * Creates a Date instance for a specific day in a month
 * @param {number} day - Day of month
 * @param {number} month - Month (0-11)
 * @param {number} year - Year
 * @returns {Date} Date instance
 */
export const createDateForDay = (day, month, year) => {
  return new Date(year, month, day);
};
